import React from 'react'

import { Flex } from '../../../components/FlexBox'
import { H5, Text } from '../../../components/common/Text'
import DraggableList from '../../../components/common/DraggableList'
import SmallButton from './SmallButton'
import PublishBar from './PublishBar'
import useTranslation from '../../../hooks/useTranslation'
import { IContentFeedBoost } from '../queries/getContentFeeds'
import BoostRow from './BoostRow'
import { TableCol, TableContainer, TableRow } from './Table'

interface FeedContentPanelProps {
  boosts: IContentFeedBoost[]
  loading: boolean
  error?: unknown
  onReorder: (next: IContentFeedBoost[]) => void
  onRemove: (boost: IContentFeedBoost) => void
  onAdd: () => void
  /** 8 for a topic feed, 3 for the hero feed. */
  maxItems: number
  /** Pending edits across the whole page, not just this panel. */
  changeCount: number
  publishing: boolean
  /** Set when the feed cannot be published yet; blocks Publish and says why. */
  publishBlockedReason?: string | null
  onPublish: () => void
  onDiscard: () => void
}

/**
 * Curated items, in the order members see them. Every edit here is a draft --
 * add, remove and reorder only change local state, and nothing reaches members
 * until Publish. The bar's count covers the feed's settings too, so a curator
 * has one place to commit and one place to back out.
 */
export const FeedContentPanel = ({
  boosts,
  loading,
  error,
  onReorder,
  onRemove,
  onAdd,
  maxItems,
  changeCount,
  publishing,
  publishBlockedReason,
  onPublish,
  onDiscard
}: FeedContentPanelProps) => {
  const { t } = useTranslation()
  const atCap = boosts.length >= maxItems

  return (
    <Flex mx={4} mb={3} flexDirection="row">
      <TableContainer>
        <Flex flexDirection="row" alignItems="center" p={2}>
          <Flex flex={1}>
            <H5 styles={{ fontWeight: '700' }}>
              {t('contentCuration:content:title')}
            </H5>
            <Text styles={{ fontSize: 12 }}>
              {t('contentCuration:content:description')}
            </Text>
          </Flex>
          <Flex flexDirection="row" alignItems="center">
            <Text styles={{ fontSize: 12, marginRight: 8 }}>
              {`${boosts.length}/${maxItems}`}
            </Text>
            <SmallButton
              label={t('contentCuration:content:add')}
              onPress={onAdd}
              disabled={atCap}
              variant="primary"
            />
          </Flex>
        </Flex>

        {!loading && !error && (
          <DraggableList
            items={boosts}
            keyExtractor={boost => `${boost.itemType}:${boost.itemId}`}
            onReorder={onReorder}
            renderItem={boost => (
              <BoostRow boost={boost} onRemove={() => onRemove(boost)} />
            )}
          />
        )}
        {loading && (
          <TableRow>
            <TableCol>
              <Text>{t('common:loading')}</Text>
            </TableCol>
          </TableRow>
        )}
        {!loading && !error && boosts.length === 0 && (
          <TableRow>
            <TableCol>
              <Text>{t('contentCuration:content:empty')}</Text>
            </TableCol>
          </TableRow>
        )}
        {!!error && (
          <TableRow>
            <TableCol>
              <Text>{t('contentCuration:content:error')}</Text>
            </TableCol>
          </TableRow>
        )}
        {atCap && (
          <TableRow>
            <TableCol>
              <Text styles={{ fontSize: 12 }}>
                {t('contentCuration:content:atCap', {
                  max: maxItems
                })}
              </Text>
            </TableCol>
          </TableRow>
        )}
        <PublishBar
          changeCount={changeCount}
          publishing={publishing}
          blockedReason={publishBlockedReason}
          onPublish={onPublish}
          onDiscard={onDiscard}
        />
      </TableContainer>
    </Flex>
  )
}

export default FeedContentPanel
